// Hook for user search functionality with debouncing and caching
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { searchUsers } from '../services/firebase/userSearch';
import { debounce } from '../utils/mentionUtils';

// LRU Cache for search results
const searchCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50;

// Recent users cache for preloading
let recentUsersCache = [];
let recentUsersCacheTime = 0;
const RECENT_USERS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Performance monitoring
let searchMetrics = {
  totalSearches: 0,
  cacheHits: 0,
  averageResponseTime: 0,
  errorCount: 0,
  abandonmentCount: 0,
  searchTerms: new Map(), // Track popular search terms
  lastResetTime: Date.now()
};

// Performance monitoring functions
const recordSearchStart = () => {
  searchMetrics.totalSearches++;
  return performance.now();
};

const recordSearchComplete = (startTime, wasFromCache = false) => {
  const responseTime = performance.now() - startTime;
  
  if (wasFromCache) {
    searchMetrics.cacheHits++;
  }
  
  // Update average response time
  const totalNonCachedSearches = searchMetrics.totalSearches - searchMetrics.cacheHits;
  if (totalNonCachedSearches > 0) {
    searchMetrics.averageResponseTime = 
      (searchMetrics.averageResponseTime * (totalNonCachedSearches - 1) + responseTime) / totalNonCachedSearches;
  }
  
  return responseTime;
};

const recordSearchError = () => {
  searchMetrics.errorCount++;
};

const recordSearchTerm = (term) => {
  if (term && term.length > 0) {
    const count = searchMetrics.searchTerms.get(term) || 0;
    searchMetrics.searchTerms.set(term, count + 1);
    
    // Keep only top 100 search terms to prevent memory bloat
    if (searchMetrics.searchTerms.size > 100) {
      const sortedTerms = Array.from(searchMetrics.searchTerms.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 100);
      searchMetrics.searchTerms = new Map(sortedTerms);
    }
  }
};

const recordSearchAbandonment = () => {
  searchMetrics.abandonmentCount++;
};

// Export metrics for monitoring
export const getSearchMetrics = () => {
  const now = Date.now();
  const timeSinceReset = now - searchMetrics.lastResetTime;
  const hoursActive = timeSinceReset / (1000 * 60 * 60);
  
  return {
    ...searchMetrics,
    cacheHitRate: searchMetrics.totalSearches > 0 
      ? ((searchMetrics.cacheHits / searchMetrics.totalSearches) * 100).toFixed(2) + '%'
      : '0%',
    errorRate: searchMetrics.totalSearches > 0
      ? ((searchMetrics.errorCount / searchMetrics.totalSearches) * 100).toFixed(2) + '%'
      : '0%',
    abandonmentRate: searchMetrics.totalSearches > 0
      ? ((searchMetrics.abandonmentCount / searchMetrics.totalSearches) * 100).toFixed(2) + '%'
      : '0%',
    searchesPerHour: hoursActive > 0 ? (searchMetrics.totalSearches / hoursActive).toFixed(2) : '0',
    topSearchTerms: Array.from(searchMetrics.searchTerms.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10),
    cacheSize: searchCache.size,
    maxCacheSize: MAX_CACHE_SIZE
  };
};

export const resetSearchMetrics = () => {
  searchMetrics = {
    totalSearches: 0,
    cacheHits: 0,
    averageResponseTime: 0,
    errorCount: 0,
    abandonmentCount: 0,
    searchTerms: new Map(),
    lastResetTime: Date.now()
  };
};

/**
 * Get cached search result with LRU management
 */
const getCachedResult = (cacheKey) => {
  const cached = searchCache.get(cacheKey);
  if (!cached) return null;
  
  const now = Date.now();
  const isExpired = now - cached.timestamp > CACHE_DURATION;
  
  if (isExpired) {
    searchCache.delete(cacheKey);
    return null;
  }
  
  // Move to end for LRU (most recently used)
  searchCache.delete(cacheKey);
  searchCache.set(cacheKey, cached);
  
  return cached.result;
};

/**
 * Set cached search result with LRU eviction
 */
const setCachedResult = (cacheKey, result) => {
  // Manage cache size with LRU eviction
  if (searchCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (first in Map)
    const firstKey = searchCache.keys().next().value;
    searchCache.delete(firstKey);
  }
  
  searchCache.set(cacheKey, {
    result,
    timestamp: Date.now()
  });
};

/**
 * Custom hook for user search with debouncing and state management
 * @param {Object} options - Hook options
 * @returns {Object} - Search state and functions
 */
export const useUserSearch = (options = {}) => {
  const {
    debounceMs = 300,
    maxResults = 10,
    minSearchLength = 0
  } = options;
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Refs
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Debounced search function with caching and performance monitoring
  const debouncedSearch = useMemo(
    () => debounce(async (term) => {
      if (!term || term.length < minSearchLength) {
        setUsers([]);
        setIsLoading(false);
        setHasSearched(false);
        return;
      }
      
      // Record search metrics
      const searchStartTime = recordSearchStart();
      recordSearchTerm(term);
      
      // Check cache first
      const cacheKey = `${term.toLowerCase()}_${maxResults}`;
      const cachedResult = getCachedResult(cacheKey);
      
      if (cachedResult) {
        // Return cached results in ~50ms
        setTimeout(() => {
          recordSearchComplete(searchStartTime, true);
          
          if (cachedResult.success) {
            setUsers(cachedResult.data);
            setHasSearched(true);
          } else {
            setError(cachedResult.error || 'Search failed');
            setUsers([]);
          }
          setIsLoading(false);
        }, 50);
        return;
      }
      
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setIsLoading(true);
      setError(null);
      
      try {
        const result = await searchUsers(term, maxResults);
        
        // Check if request was aborted
        if (abortControllerRef.current?.signal.aborted) {
          recordSearchAbandonment();
          return;
        }
        
        // Record successful search
        recordSearchComplete(searchStartTime, false);
        
        // Cache the result
        setCachedResult(cacheKey, result);
        
        if (result.success) {
          setUsers(result.data);
          setHasSearched(true);
        } else {
          setError(result.error || 'Search failed');
          setUsers([]);
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name !== 'AbortError') {
          recordSearchError();
          console.error('User search error:', err);
          
          // Try to fallback to cached results
          const fallbackCacheKey = `${term.toLowerCase()}_${maxResults}`;
          const staleResult = searchCache.get(fallbackCacheKey);
          
          if (staleResult) {
            console.log('Using stale cached results due to error');
            if (staleResult.result.success) {
              setUsers(staleResult.result.data);
              setHasSearched(true);
              setError('Showing cached results (network unavailable)');
            } else {
              setError('Search temporarily unavailable');
              setUsers([]);
            }
          } else {
            setError('Search temporarily unavailable');
            setUsers([]);
          }
        } else {
          recordSearchAbandonment();
        }
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }, debounceMs),
    [debounceMs, maxResults, minSearchLength]
  );
  
  // Preload recent users when search starts
  const preloadRecentUsers = useCallback(async () => {
    const now = Date.now();
    
    // Check if we have recent cached users
    if (recentUsersCache.length > 0 && (now - recentUsersCacheTime) < RECENT_USERS_CACHE_DURATION) {
      return recentUsersCache;
    }
    
    try {
      // Fetch recent users (empty search to get most active)
      const result = await searchUsers('', 10);
      if (result.success) {
        recentUsersCache = result.data;
        recentUsersCacheTime = now;
        return recentUsersCache;
      }
    } catch (error) {
      console.debug('Failed to preload recent users:', error);
    }
    
    return [];
  }, []);

  // Search function with preloading
  const search = useCallback((term) => {
    const trimmedTerm = term?.trim() || '';
    setSearchTerm(trimmedTerm);
    
    // Clear timeout if exists
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (trimmedTerm.length < minSearchLength) {
      // Show recent users when no search term
      if (trimmedTerm === '') {
        preloadRecentUsers().then(recentUsers => {
          if (recentUsers.length > 0) {
            setUsers(recentUsers);
            setHasSearched(true);
          }
        }).catch(err => {
          console.debug('Failed to load recent users:', err);
        });
      } else {
        setUsers([]);
        setHasSearched(false);
      }
      setIsLoading(false);
      setError(null);
      return;
    }
    
    // Set loading immediately for immediate feedback
    setIsLoading(true);
    setError(null);
    
    debouncedSearch(trimmedTerm);
  }, [minSearchLength, preloadRecentUsers, debouncedSearch]);
  
  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setUsers([]);
    setIsLoading(false);
    setError(null);
    setHasSearched(false);
    
    // Cancel pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  // Retry last search
  const retrySearch = useCallback(() => {
    if (searchTerm) {
      setError(null);
      search(searchTerm);
    }
  }, [searchTerm, search]);
  
  // Immediate search (bypass debouncing)
  const searchImmediate = useCallback(async (term) => {
    const trimmedTerm = term?.trim() || '';
    
    if (trimmedTerm.length < minSearchLength) {
      clearSearch();
      return;
    }
    
    setSearchTerm(trimmedTerm);
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await searchUsers(trimmedTerm, maxResults);
      
      if (result.success) {
        setUsers(result.data);
        setHasSearched(true);
      } else {
        setError(result.error || 'Search failed');
        setUsers([]);
      }
    } catch (err) {
      console.error('Immediate user search error:', err);
      setError('Search failed. Please try again.');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [maxResults, minSearchLength, clearSearch]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  
  // Derived state
  const hasResults = users.length > 0;
  const showNoResults = hasSearched && !isLoading && !hasResults && !error;
  const showResults = hasResults && !isLoading;
  
  return {
    // State
    searchTerm,
    users,
    isLoading,
    error,
    hasSearched,
    hasResults,
    showNoResults,
    showResults,
    
    // Functions
    search,
    searchImmediate,
    clearSearch,
    retrySearch,
    
    // Computed
    isEmpty: users.length === 0,
    isSearching: isLoading,
    canSearch: searchTerm.length >= minSearchLength
  };
};

export default useUserSearch;